// Team Utilisation for the AU book — same layout as the UK tab, fed by the caseload Activity data (Aircall AU numbers,
// AU cases, promises) for today / yesterday / this week / this month, Sydney clock. Click a member to open their timeline.
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Briefcase, ChevronDown, ChevronUp, Clock, Handshake, Loader2, Mail, MessageSquare, Phone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/regionFmt";
import { getCollectorActivity, getCollectorCash } from "@/lib/api";
import { PERIODS } from "./CollectorScoreboard";
import { CollectorTimeline } from "./CollectorTimeline";

const money0 = (v) => fmtMoney(v || 0, 0);
const fmtMins = (sec) => { const m = Math.floor((sec || 0) / 60); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function HourlyStrip({ hourly = [] }) {
  const max = Math.max(1, ...hourly);
  return (
    <div className="flex items-end gap-[2px]" title="Activity by hour (00–23, Sydney time): calls, SMS, emails, promises">
      {hourly.map((v, h) => (
        <div key={h} className="flex flex-col items-center">
          <div className={`w-2 rounded-sm ${v > 0 ? "bg-amber-500" : "bg-slate-200"}`} style={{ height: `${4 + (v / max) * 24}px`, opacity: v > 0 ? 0.35 + (v / max) * 0.65 : 1 }} />
          {h % 6 === 0 && <span className="text-[8px] text-muted-foreground mt-0.5">{String(h).padStart(2, "0")}</span>}
        </div>
      ))}
    </div>
  );
}

function MemberRow({ r, cash, period }) {
  const [open, setOpen] = useState(false);
  const kept = r.p2p_kept + r.p2p_broken;
  const keptPct = kept ? Math.round((100 * r.p2p_kept) / kept) : null;
  return (
    <Card className="border-slate-200" data-testid={`au-team-member-${slug(r.collector)}`}>
      <CardContent className="py-3 px-4">
        <div className="grid grid-cols-2 md:grid-cols-8 gap-3 items-center">
          <div className="md:col-span-2">
            <p className="font-semibold text-sm">{r.collector}{!r.in_roster && <span className="ml-1 text-[9px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 align-middle" title="Seen on Aircall / the files but not a portal user of the AU region">not in roster</span>}</p>
            <p className="text-xs text-muted-foreground">{r.files} files • {money0(r.arrears_book)} arrears{r.avg_dpd != null ? ` • ${r.avg_dpd}d avg DPD` : ""}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><Phone className="w-3 h-3" /> Calls</p>
            <p className="text-sm font-bold">{r.calls_made}</p>
            <p className="text-[10px] text-muted-foreground">{r.calls_answered} answered{r.answer_rate != null ? ` (${r.answer_rate}%)` : ""} • {r.calls_inbound} in • {fmtMins(r.talk_time_secs)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><MessageSquare className="w-3 h-3" /> SMS</p>
            <p className="text-sm font-bold">{r.sms_sent}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><Mail className="w-3 h-3" /> Emails</p>
            <p className="text-sm font-bold">{r.emails_sent}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><Handshake className="w-3 h-3" /> Promises</p>
            <p className="text-sm font-bold">{r.p2p_taken}{r.p2p_taken > 0 && <span className="text-xs font-normal text-slate-400 ml-1">{money0(r.p2p_taken_amount)}</span>}</p>
            <p className="text-[10px] text-muted-foreground">
              <span className="text-emerald-700">{r.p2p_kept} kept</span> • <span className="text-rose-700">{r.p2p_broken} broken</span>
              {keptPct != null && <Badge className={`ml-1 px-1 py-0 text-[9px] ${keptPct >= 60 ? "bg-green-100 text-green-700 border-green-300" : "bg-amber-100 text-amber-700 border-amber-300"}`}>{keptPct}%</Badge>}
            </p>
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Cash on their files</p>
              <p className="text-sm font-bold text-emerald-700">{cash === undefined ? <Loader2 className="w-3 h-3 animate-spin inline text-slate-300" /> : cash === null ? "—" : money0(cash.cash)}</p>
            </div>
            <HourlyStrip hourly={r.hourly} />
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setOpen(!open)} data-testid={`au-team-expand-${slug(r.collector)}`}>
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {open && <div className="mt-3 pt-3 border-t"><CollectorTimeline collector={r.collector} period={period} onBack={() => setOpen(false)} backLabel="Collapse" /></div>}
      </CardContent>
    </Card>
  );
}

export default function AuTeamUtilisation() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [cash, setCash] = useState(undefined);   // undefined = loading, null = failed, object = by collector
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true); setCash(undefined);
    getCollectorActivity(period).then(setData).catch((e) => toast.error("Team utilisation: " + (e?.response?.data?.detail || e.message))).finally(() => setLoading(false));
    getCollectorCash(period).then((c) => setCash(Object.fromEntries((c.rows || []).map((r) => [r.collector, r])))).catch(() => setCash(null));
  };
  useEffect(load, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = data?.total;
  const rows = data?.rows || [];
  const cashFor = (r) => (cash === undefined ? undefined : cash === null ? null : (cash[r.collector] || { cash: 0, payments: 0 }));
  const cashTotal = cash && Object.values(cash).reduce((a, x) => a + (x.cash || 0), 0);
  const asOf = data?.as_of ? new Date(data.as_of).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <div className="space-y-4" data-testid="au-team-utilisation-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-amber-700 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Team Utilisation <span className="text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-50 px-2 py-0.5 ring-1 ring-amber-200 text-amber-800">🇦🇺 AU</span></h3>
          <p className="text-xs text-muted-foreground">Calls, SMS, emails and promises per collector on the AU book — Aircall AU numbers, Sydney clock{asOf ? ` • as of ${asOf}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border bg-white p-0.5 text-xs" data-testid="au-team-period">
            {PERIODS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriod(k)} className={`px-2.5 py-1 rounded ${period === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`au-team-period-${k}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="au-team-refresh">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Building utilisation report...</div>
      ) : !rows.length ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />No AU collector activity {PERIODS.find(([k]) => k === period)?.[1]?.toLowerCase()}</CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="au-team-totals">
            {[
              ["Team members", rows.length],
              ["Calls made", `${t.calls_made}${t.answer_rate != null ? ` · ${t.answer_rate}%` : ""}`],
              ["Talk time", fmtMins(t.talk_time_secs)],
              ["SMS sent", t.sms_sent],
              ["Emails", t.emails_sent],
              ["Promises taken", `${t.p2p_taken}${t.p2p_taken ? ` · ${money0(t.p2p_taken_amount)}` : ""}`],
              ["Arrears book", money0(t.arrears_book)],
              ["Cash collected", cash === undefined ? "…" : cash === null ? "—" : money0(cashTotal)],
            ].map(([label, value]) => (
              <Card key={label} className="bg-amber-50/50 border-amber-100">
                <CardContent className="py-2.5 px-3">
                  <p className="text-[10px] text-amber-700 uppercase">{label}</p>
                  <p className="text-lg font-bold text-amber-900 truncate" title={String(value)}>{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="space-y-2">{rows.map((r) => <MemberRow key={r.collector} r={r} cash={cashFor(r)} period={period} />)}</div>
          {cash === null && <p className="text-[11px] text-amber-700" data-testid="au-team-cash-failed">Cash collected couldn&apos;t be loaded from LoanDisk AU just now — the rest of the board is live.</p>}
        </>
      )}
    </div>
  );
}
